import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ShareButtons from "@/components/base/ShareButtons";
import ArticleBody from "@/components/feature/ArticleBody";
import { resourceList } from "@/mocks/resources";
import usePageTitle from "@/hooks/usePageTitle";

export default function ResourceDetail() {
  const { id } = useParams();
  const resource = resourceList.find((item) => item.id === id);
  const [notified, setNotified] = useState(false);
  usePageTitle(resource ? `资源详情 - ${resource.title}` : "资源查询");

  if (!resource) {
    return (
      <div className="px-4 md:px-8 py-16">
        <div className="mx-auto max-w-3xl">
          <EmptyState
            title="未找到该资源"
            description="这项资源可能已更新或链接有误，返回资源查询页浏览全部资源。"
            actionLabel="返回资源查询"
            actionTo="/resources"
          />
        </div>
      </div>
    );
  }

  const related = resourceList
    .filter((item) => item.id !== resource.id && item.field === resource.field)
    .concat(resourceList.filter((item) => item.id !== resource.id && item.field !== resource.field))
    .slice(0, 3);

  const meta = [
    { label: "资源类型", value: resource.type },
    { label: "相关领域", value: resource.field },
    { label: "文件格式", value: resource.format },
    { label: "文件大小", value: resource.size },
    { label: "更新时间", value: resource.updatedAt },
  ];

  return (
    <article className="w-full">
      <section className="border-b border-background-200 bg-background-100 px-4 md:px-8 py-8 md:py-10">
        <div className="mx-auto max-w-4xl">
          <Breadcrumb
            items={[{ label: "资源查询", to: "/resources" }, { label: resource.title }]}
          />
        </div>
      </section>

      <div className="px-4 md:px-8 py-10 md:py-14">
        <div className="mx-auto max-w-4xl flex flex-col gap-8">
          <header className="flex flex-col gap-5">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
              {resource.type}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold leading-snug text-foreground-950">
              {resource.title}
            </h1>
            <p className="text-sm md:text-base leading-relaxed text-foreground-600">
              {resource.summary}
            </p>
          </header>

          <div className="h-64 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src={resource.cover}
              alt={resource.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-3 lg:grid-cols-5">
            {meta.map((item) => (
              <div key={item.label} className="flex flex-col gap-1.5">
                <span className="text-xs text-foreground-400">{item.label}</span>
                <span className="text-sm font-medium text-foreground-900">{item.value}</span>
              </div>
            ))}
          </div>

          <ArticleBody sections={resource.content} />

          <div className="flex flex-wrap gap-2">
            {resource.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex flex-col gap-4 rounded-lg border border-dashed border-accent-300 bg-accent-50/70 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-md bg-accent-500/15 text-accent-600">
                <i className="ri-download-cloud-2-line text-xl" />
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-foreground-900">
                  {resource.format === "网页" ? "在线查阅" : "下载资源"}
                </span>
                <span className="text-xs text-foreground-500">
                  当前为前端演示功能，暂未接入真实文件服务，暂无实际文件可下载。
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setNotified(true)}
              className="inline-flex shrink-0 items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className={resource.format === "网页" ? "ri-eye-line" : "ri-download-line"} />
              {resource.format === "网页" ? "在线查看" : "下载文件"}
            </button>
          </div>
          {notified && (
            <p className="rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-600">
              <i className="ri-information-line mr-2 text-accent-500" />
              演示提示：该功能为前端模拟，正式的文件下载与在线查阅将在后端资源服务接入后开放。
            </p>
          )}

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <ShareButtons title={resource.title} />
          </div>

          <section className="flex flex-col gap-5">
            <h2 className="font-heading text-xl font-semibold text-foreground-950">
              推荐相似资源
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/resources/${item.id}`}
                  className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <div className="h-28 w-full overflow-hidden rounded-md">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <span className="text-xs font-medium text-accent-600">{item.type}</span>
                  <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-600">
                    {item.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>

          <Link
            to="/resources"
            className="inline-flex w-fit items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回资源查询
          </Link>
        </div>
      </div>
    </article>
  );
}